import { current } from "@reduxjs/toolkit";
import { isAdmin } from "src/Common/function";
import api from "./api";
import UserService from "./user.service";

//This function is for admin
// `filters` (optional): { customerIds, branchIds, locationIds, technicianIds }.
// Empty arrays mean "all" for that dimension; the backend treats them as no-ops.
const sendWOReport = (
  recipients,
  all,
  past7Days,
  past30Days,
  customDays,
  customStartDate,
  customEndDate,
  date,
  type,
  filters = {}
) => {
  return api.post("/api/wo/send_wo_report", {
    recipients,
    all,
    past7Days,
    past30Days,
    customDays,
    customStartDate,
    customEndDate,
    date,
    type,
    ...filters,
  });
};

const sendHoursWorkedReport = (
  branchId,
  recipients,
  past7Days,
  past30Days,
  customDays,
  customStartDate,
  customEndDate,
  date,
  filters = {}
) => {
  return api.post("/api/report/send_hours_worked_report", {
    branchId,
    recipients,
    past7Days,
    past30Days,
    customDays,
    customStartDate,
    customEndDate,
    date,
    ...filters,
  });
};

// The preview endpoints return one page of rows at a time. `page` is 0-based;
// the response carries `recordsFound` (the full match count, not just this
// page), `rows`, `page`, and `pageSize`.
const getWOReportPreview = (
  all,
  past7Days,
  past30Days,
  customDays,
  customStartDate,
  customEndDate,
  date,
  type,
  filters = {},
  page = 0
) => {
  return api.post("/api/wo/wo_report_preview", {
    all,
    past7Days,
    past30Days,
    customDays,
    customStartDate,
    customEndDate,
    date,
    type,
    ...filters,
    page,
  });
};

const getHoursWorkedReportPreview = (
  branchId,
  past7Days,
  past30Days,
  customDays,
  customStartDate,
  customEndDate,
  date,
  filters = {},
  page = 0
) => {
  return api.post("/api/report/hours_worked_report_preview", {
    branchId,
    past7Days,
    past30Days,
    customDays,
    customStartDate,
    customEndDate,
    date,
    ...filters,
    page,
  });
};

// `args`: { past7Days, past30Days, customDays, customStartDate, customEndDate,
// woNumber, page, filters: { customerIds, branchIds, locationIds, technicianIds } }.
// Empty filter arrays mean "all"; an empty woNumber means "all work orders".
// `page` is 0-based and selects which page of rows the preview returns.
// Same hours data as getHoursWorkedReportPreview, on the Time Management route
// so it is gated on time-management:read rather than on ad-hoc reporting
// rights. Payroll staff should not need report generation to read hours.
const getTimeManagementHours = (
  branchId,
  past7Days,
  past30Days,
  customDays,
  customStartDate,
  customEndDate,
  date,
  filters = {}
) => {
  // No page argument: this endpoint returns the whole range in one response.
  return api.post("/api/report/time_management_hours", {
    branchId,
    past7Days,
    past30Days,
    customDays,
    customStartDate,
    customEndDate,
    date,
    ...filters,
  });
};

const getRefrigerantReportPreview = (args) => {
  const { filters = {}, ...rest } = args;
  return api.post("/api/report/refrigerant_report_preview", { ...rest, ...filters });
};

const sendRefrigerantReport = (args) => {
  const { filters = {}, ...rest } = args;
  return api.post("/api/report/send_refrigerant_report", { ...rest, ...filters });
};

// `args`: { past7Days, past30Days, customDays, customStartDate, customEndDate,
// date, woNumber, page, filters: { customerIds, branchIds, locationIds, technicianIds } }.
// Empty filter arrays mean "all"; an empty woNumber means "all work orders".
// `page` is 0-based and selects which page of rows the preview returns.
const getRecommendationReportPreview = (args) => {
  const { filters = {}, ...rest } = args;
  return api.post("/api/report/recommendation_report_preview", { ...rest, ...filters });
};

const sendRecommendationReport = (args) => {
  const { filters = {}, ...rest } = args;
  return api.post("/api/report/send_recommendation_report", { ...rest, ...filters });
};

const create = (data) => {
  return api.post("/api/report/create", data);
};

const getAll = (branch) => {
  // let currentUser = UserService.getCurrentUser();
  // let branchId = branch;
  // if (!isAdmin()) {
  //   branchId = currentUser.branches[0].id;
  // }
  return api.get("/api/report/get_all", { params: { branchId: branch } });
};

const deleteReport = (id) => {
  return api.get("/api/report/delete", { params: { id } });
};

const update = (id, data) => {
  return api.post("/api/report/update", { id, data });
};

// Record — or undo — payroll's decision to measure one technician-week as two,
// because the hours span branches on both week calendars. Its own endpoint and
// permission: this changes what a person is paid, which is not the same
// capability as being allowed to look at payroll.
const setCycleSplit = (technicianId, weekStart, split) => {
  return api.post("/api/time-management/cycle_split", { userId: technicianId, weekStart, split });
};

// One technician's hours history for the days in a range: who changed what,
// from where, and why. Read-only; the ledger is written server-side on every
// path that saves hours, so the client never decides what counts as a change.
const getHoursEvents = (userId, startDate, endDate) => {
  return api.get("/api/time-management/hours_events", { params: { userId, startDate, endDate } });
};

// Every loaded technician's history in one request, so a drawer opens from
// memory rather than after a round trip. Each row carries subjectUserId.
// `by` selects which date the range filters on: "date" (the days the hours
// belong to — the payroll screen) or "created" (when the change was made — a
// person's activity timeline, where a removed day must still be findable).
const getHoursEventsFor = (userIds, startDate, endDate, by = "date") => {
  return api.get("/api/time-management/hours_events", {
    params: { userIds: userIds.join(","), startDate, endDate, by },
  });
};

// Lunch recording rules, per branch. The list is read-gated like the rest of
// the screen; changing one is its own permission, because it changes what is
// demanded of every technician on that branch. Absent key = leave alone.
// The weekly sign-off switch, per branch. Reading rides on the screen's
// permission; flipping one is its own, because it changes what is demanded
// of every technician on the branch and what is locked. A reason is required
// and is written to each technician's hours history.
// Which hours system each person is on, grouped by branch. Switching a
// branch moves every technician in it; switching one person moves only them.
// Reason required; written to each person's hours history.
const getHoursSystem = () => {
  return api.get("/api/time-management/hours_system");
};

const setHoursSystemForBranch = (branchId, derived, reason) => {
  return api.post("/api/time-management/hours_system", { branchId, derived, reason });
};

const setHoursSystemForUser = (userId, derived, reason) => {
  return api.post("/api/time-management/hours_system_user", { userId, derived, reason });
};

const getSignoffRules = () => {
  return api.get("/api/time-management/signoff_rules");
};

const setSignoffRule = (branchId, enabled, reason) => {
  return api.post("/api/time-management/signoff_rules", { branchId, enabled, reason });
};

const getLunchRules = () => {
  return api.get("/api/time-management/lunch_rules");
};

const setLunchRule = (branchId, patch = {}) => {
  const body = { branchId };
  if (patch.allowed !== undefined) body.allowed = patch.allowed;
  if (patch.required !== undefined) body.required = patch.required;
  return api.post("/api/time-management/lunch_rules", body);
};

// Every loaded technician's on-call spans touching the range, in one request.
// Read-only here: on call is recorded from the tech app and, later, scheduled
// from the office. It is never part of the hours rows — it counts toward
// nothing — so it travels beside them the way the history does.
const getOnCallFor = (userIds, startDate, endDate) => {
  return api.get("/api/time-management/on_call", {
    params: { userIds: userIds.join(","), startDate, endDate },
  });
};

// Weekly sign-off. The technician signs last week from the app; these are
// the office's side: who has and hasn't, and the decisions about it. Each
// write is its own permission, since each changes what a person can do to
// their own hours or what payroll works from.
const getTimecardSubmissionsFor = (userIds, startDate, endDate) => {
  return api.get("/api/time-management/timecard_submissions", {
    params: { userIds: userIds.join(","), startDate, endDate },
  });
};

const decideTimecardReopen = (userId, weekStart, approve, reason = null) => {
  return api.post("/api/time-management/timecard_submissions/decide", { userId, weekStart, approve, reason });
};

const reopenTimecard = (userId, weekStart, reason) => {
  return api.post("/api/time-management/timecard_submissions/reopen", { userId, weekStart, reason });
};

const submitTimecardFor = (userId, weekStart, reason) => {
  return api.post("/api/time-management/timecard_submissions/submit_for", { userId, weekStart, reason });
};

const remindTimecard = (userId, weekStart, channels) => {
  return api.post("/api/time-management/timecard_submissions/remind", { userId, weekStart, channels });
};

const ReportService = {
  getTimecardSubmissionsFor,
  getHoursSystem,
  setHoursSystemForBranch,
  setHoursSystemForUser,
  getSignoffRules,
  setSignoffRule,
  decideTimecardReopen,
  reopenTimecard,
  submitTimecardFor,
  remindTimecard,
  getOnCallFor,
  getLunchRules,
  setLunchRule,
  setCycleSplit,
  getHoursEvents,
  getHoursEventsFor,
  sendWOReport,
  sendHoursWorkedReport,
  getWOReportPreview,
  getHoursWorkedReportPreview,
  getTimeManagementHours,
  getRefrigerantReportPreview,
  sendRefrigerantReport,
  getRecommendationReportPreview,
  sendRecommendationReport,
  create,
  getAll,
  deleteReport,
  update,
};

export default ReportService;
